/**
 * Special moves — per-hero unique attacks beyond the standard combo system.
 * Pure module: no Phaser import. Combat logic layer reads these defs.
 *
 * RAY "TANK" MILLER (MY-1441):
 *   Glasgow Kiss — running headbutt that applies extended hitstun (stun).
 *   Lifer        — pile-driver; instant KO, only valid on a stunned target.
 *
 * ERROL "DREAD" CLARKE (MY-1442):
 *   Iron Lion    — home-run cricket-bat swing; multi-hit sweep across all
 *                  enemies in a horizontal line; sends target across screen.
 *   Stump Throw  — hurls a cricket stump as a ranged projectile.
 *
 * PRIYA "FURY" KAUR (MY-1444):
 *   Cobra Strike — flaming corkscrew jump-kick; aerial finisher.
 *   Medic        — heal partner once/stage + self-heal once/stage.
 *
 * GHOST (MY-1445):
 *   Stim Shot    — auto-injector; brief invincibility + speed boost (self-buff).
 *   Smoke        — smoke canister thrown forward; creates a blind zone.
 *   Flashbang    — stun grenade; stuns every on-screen enemy for 3 s.
 * KRIS KOWALCZYK (MY-1443):
 *   Wisła Sweep        — 270° hammer spin sweep (multiHitLine) + ground stomp.
 *   Parry Counter Throw — Sambo over-the-shoulder throw; only valid inside
 *                         the parry counter window (BlockParry system).
 */
import type { HitSpec } from '@/systems/Combat';

/** A special move definition. */
export interface SpecialDef {
  id: string;
  /** Animation key token — wired to spritesheet when Soul-ID art lands. */
  animKey: string;
  /** Hit parameters applied on connect. */
  spec: HitSpec;
  /** Horizontal reach of the activation zone from the attacker centre (px). */
  range: number;
  /** If true, activation requires the target to be in hitstun. */
  requiresStunned: boolean;
  /**
   * If true, the hitbox sweeps the entire horizontal line — all enemies in
   * range are hit (Iron Lion multi-hit behaviour).
   */
  multiHitLine?: boolean;
  /** If true, attack launches a projectile rather than a melee hitbox. */
  isProjectile?: boolean;
  /** If true, the move is executed airborne (jump-kick / aerial finisher). */
  isAerial?: boolean;
  /** If true, the move restores HP rather than dealing damage. */
  isHeal?: boolean;
  /** HP restored to the partner when isHeal is true. */
  healAmount?: number;
  /** HP restored to self when isHeal is true. */
  selfHealAmount?: number;
  /** Maximum times this special may be used in a single stage. */
  usesPerStage?: number;
  /**
   * If true, this special is a non-damaging self-buff rather than an attack.
   * Runtime: skip hitbox/damage processing; apply buff effects instead.
   */
  isBuff?: boolean;
  /**
   * Frames of invincibility (i-frames) granted to the caster on activation.
   * Requires isBuff — consumed by Player state machine.
   */
  invincibilityFrames?: number;
  /**
   * Frames of speed boost granted to the caster on activation.
   * Requires isBuff — consumed by Player movement system.
   */
  speedBoostFrames?: number;
  /**
   * Speed multiplier applied during the speed-boost window (e.g. 1.4 = 40% faster).
   * Requires speedBoostFrames.
   */
  speedBoostMult?: number;
  /**
   * If true, the projectile creates a persistent smoke/blind zone at impact.
   * Requires isProjectile — consumed by StageScene fx layer.
   * TODO(MY-1445): wire blind-zone visual + enemy-AI suppression in StageScene.
   */
  isSmokeScreen?: boolean;
  /**
   * If true, the move ends with a ground stomp that deals a secondary shockwave
   * to all grounded enemies in range (Wisła Sweep phase 2).
   */
  groundStomp?: boolean;
  /**
   * If true, the move may only be activated while inside the parry counter
   * window opened by a successful BlockParry tap (KRIS parry-counter-throw).
   */
  requiresParryCounter?: boolean;
}

/**
 * Glasgow Kiss — running headbutt.
 * Very long hitstun sets the target up for a Lifer pile-driver follow-up.
 */
export const GLASGOW_KISS: SpecialDef = {
  id: 'glasgowKiss',
  animKey: 'special_ray_glasgowKiss',
  spec: { damage: 20, knockback: 20, hitstun: 55 },
  range: 72,
  requiresStunned: false,
};

/**
 * Lifer — pile-driver.
 * Damage of 9999 guarantees an instant KO regardless of remaining HP.
 * Only activates against a stunned (hitstunned) target.
 */
export const LIFER: SpecialDef = {
  id: 'lifer',
  animKey: 'special_ray_lifer',
  spec: { damage: 9999, knockback: 0, hitstun: 0 },
  range: 65,
  requiresStunned: true,
};

/** Frames of hitstun Glasgow Kiss applies (consumed by tests + HUD). */
export const GLASGOW_KISS_STUN_FRAMES = GLASGOW_KISS.spec.hitstun;

/**
 * Iron Lion — home-run cricket-bat swing (ERROL "DREAD" CLARKE, MY-1442).
 * Sweeps through all enemies in a horizontal line (multiHitLine) and sends
 * the primary target flying across the screen with extreme knockback.
 */
export const IRON_LION: SpecialDef = {
  id: 'ironLion',
  animKey: 'special_errol_ironLion',
  spec: { damage: 22, knockback: 85, hitstun: 22 },
  range: 68,
  requiresStunned: false,
  multiHitLine: true,
};

/**
 * Stump Throw — ranged cricket-stump projectile (ERROL "DREAD" CLARKE, MY-1442).
 * Launches a stump along the ground; hits all enemies in its travel path.
 */
export const STUMP_THROW: SpecialDef = {
  id: 'stumpThrow',
  animKey: 'special_errol_stumpThrow',
  spec: { damage: 14, knockback: 25, hitstun: 18 },
  range: 200,
  requiresStunned: false,
  isProjectile: true,
};

/**
 * Cobra Strike — flaming corkscrew jump-kick (PRIYA "FURY" KAUR, MY-1444).
 * Aerial finisher; launches Priya airborne into a spinning kick. Primary
 * combo-ender after an aerial juggle sequence.
 */
export const COBRA_STRIKE: SpecialDef = {
  id: 'cobraStrike',
  animKey: 'special_priya_cobraStrike',
  spec: { damage: 25, knockback: 60, hitstun: 28 },
  range: 64,
  requiresStunned: false,
  isAerial: true,
};

/**
 * Medic — healing aura (PRIYA "FURY" KAUR, MY-1444).
 * Restores HP to Priya's partner once per stage; also restores a smaller
 * amount to Priya herself once per stage. No damage dealt — pure support.
 * Runtime combat layer must track uses this stage against `usesPerStage`.
 */
export const MEDIC: SpecialDef = {
  id: 'medic',
  animKey: 'special_priya_medic',
  spec: { damage: 0, knockback: 0, hitstun: 0 },
  range: 0,
  requiresStunned: false,
  isHeal: true,
  healAmount: 30,
  selfHealAmount: 15,
  usesPerStage: 1,
};

/** HP Medic restores to the partner per use. */
export const MEDIC_HEAL_AMOUNT = MEDIC.healAmount as number;

/** HP Medic restores to Priya per use. */
export const MEDIC_SELF_HEAL_AMOUNT = MEDIC.selfHealAmount as number;

/**
 * Stim Shot — auto-injector self-buff (GHOST, MY-1445).
 * Grants brief invincibility and a speed boost. Restores minor HP (the
 * injector fights shock/blood loss). No hitbox — pure caster buff.
 * TODO(MY-1445): wire invincibilityFrames + speedBoostFrames in Player.ts.
 */
export const STIM_SHOT: SpecialDef = {
  id: 'stimShot',
  animKey: 'special_ghost_stimShot',
  spec: { damage: 0, knockback: 0, hitstun: 0 },
  range: 0,
  requiresStunned: false,
  isBuff: true,
  selfHealAmount: 10,
  invincibilityFrames: 90,
  speedBoostFrames: 120,
  speedBoostMult: 1.4,
  usesPerStage: 2,
};

/** Frames of invincibility Stim Shot grants. */
export const STIM_SHOT_INVINCIBILITY_FRAMES = STIM_SHOT.invincibilityFrames as number;

/** Frames of speed boost Stim Shot grants. */
export const STIM_SHOT_SPEED_BOOST_FRAMES = STIM_SHOT.speedBoostFrames as number;

/** Speed multiplier applied during Stim Shot's boost window. */
export const STIM_SHOT_SPEED_MULT = STIM_SHOT.speedBoostMult as number;

/**
 * Smoke — smoke canister thrown forward (GHOST, MY-1445).
 * Creates a persistent blind zone at impact; disorients enemies in range.
 * TODO(MY-1445): wire isSmokeScreen blind-zone fx + AI suppression in StageScene.
 */
export const SMOKE: SpecialDef = {
  id: 'smoke',
  animKey: 'special_ghost_smoke',
  spec: { damage: 5, knockback: 10, hitstun: 25 },
  range: 160,
  requiresStunned: false,
  isProjectile: true,
  isSmokeScreen: true,
  usesPerStage: 2,
};

/**
 * Flashbang — stun grenade (GHOST, MY-1445).
 * Thrown forward; detonates and stuns every on-screen enemy for 3 s
 * (180 frames at 60 fps). One use per stage — extremely powerful.
 */
export const FLASHBANG: SpecialDef = {
  id: 'flashbang',
  animKey: 'special_ghost_flashbang',
  spec: { damage: 8, knockback: 15, hitstun: 180 },
  range: 320,
  requiresStunned: false,
  isProjectile: true,
  multiHitLine: true,
  usesPerStage: 1,
};

/** Hitstun Flashbang applies to all on-screen enemies (3 s at 60 fps). */
export const FLASHBANG_STUN_FRAMES = FLASHBANG.spec.hitstun;

/**
 * Wisła Sweep — 270° rotating hammer spin (KRIS KOWALCZYK, MY-1443).
 * Sweeps through all enemies in the horizontal line (multiHitLine) then
 * slams the hammer down in a ground stomp that radiates a shockwave.
 * Primary hit sends the nearest enemy flying; stomp catches grounded targets.
 */
export const WISLA_SWEEP: SpecialDef = {
  id: 'wislaSweep',
  animKey: 'special_kris_wislaSweep',
  spec: { damage: 26, knockback: 50, hitstun: 28 },
  range: 72,
  requiresStunned: false,
  multiHitLine: true,
  groundStomp: true,
};

/**
 * Parry Counter Throw — Sambo-style over-the-shoulder throw (KRIS KOWALCZYK, MY-1443).
 * Only activates inside the parry counter window after a successful block parry.
 * High knockback — target travels across the screen and can collide with bystanders.
 */
export const PARRY_COUNTER_THROW: SpecialDef = {
  id: 'parryCounterThrow',
  animKey: 'special_kris_parryCounterThrow',
  spec: { damage: 18, knockback: 65, hitstun: 35 },
  range: 70,
  requiresStunned: false,
  requiresParryCounter: true,
};

const HERO_SPECIALS: Record<string, readonly SpecialDef[]> = {
  ray: [GLASGOW_KISS, LIFER],
  errol: [IRON_LION, STUMP_THROW],
  priya: [COBRA_STRIKE, MEDIC],
  ghost: [STIM_SHOT, SMOKE, FLASHBANG],
  kris: [WISLA_SWEEP, PARRY_COUNTER_THROW],
};

const EMPTY: readonly SpecialDef[] = [];

/** Return special moves for the given hero, or an empty array if none defined. */
export function getHeroSpecials(heroId: string): readonly SpecialDef[] {
  return HERO_SPECIALS[heroId] ?? EMPTY;
}

/**
 * Guard for Lifer activation.
 * The target must be in hitstun (typically applied by Glasgow Kiss first).
 */
export function canActivateLifer(targetIsStunned: boolean): boolean {
  return targetIsStunned;
}

/**
 * Guard for Medic activation.
 * The special has a per-stage usage cap; the caller tracks uses this stage.
 */
export function canActivateMedic(usesThisStage: number): boolean {
  return usesThisStage < (MEDIC.usesPerStage ?? 1);
}

/**
 * Guard for Stim Shot activation.
 * Two uses per stage; caller tracks uses.
 */
export function canActivateStimShot(usesThisStage: number): boolean {
  return usesThisStage < (STIM_SHOT.usesPerStage ?? 2);
}

/**
 * Guard for Flashbang activation.
 * One use per stage; caller tracks uses.
 */
export function canActivateFlashbang(usesThisStage: number): boolean {
  return usesThisStage < (FLASHBANG.usesPerStage ?? 1);
}

/**
 * Guard for Parry Counter Throw activation.
 * Requires the parry counter window to be open (opened by a successful parry).
 */
export function canActivateParryCounterThrow(isInCounterWindow: boolean): boolean {
  return isInCounterWindow;
}
